import React, { useState, useMemo, useEffect } from 'react';
import {
  Typography,
  Box,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  Tab,
  Tabs,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  OutlinedInput,
  FormHelperText,
} from '@mui/material';
import {
  Person as PersonIcon,
  School as SchoolIcon,
  Schedule as ScheduleIcon,
  History as HistoryIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateTimeInViewerTZ, getViewerTimezone } from '../../utils/dateTimeUtils';
import { DemoCountryFullLines } from '../../components/Demos/DemoCountrySchedule';
import { hasMeetingUrl, normalizeMeetingUrlInput } from '../../utils/meetingUrl';
import StatusUpdateDialog from '../Leads/StatusUpdateDialog';
import EnrollmentLinkDialog from '../Leads/EnrollmentLinkDialog';
import { demosService } from '../../services/demos';
import { leadsAPI } from '../../services/api';
import { getCountries } from '../../data/locationData';
import { formatDemoInstructorLabel } from '../../utils/demoInstructorLabel';
import { formatChildAgeLabel } from '../Leads/leadDisplayUtils';
import toast from 'react-hot-toast';

function channelBadge(channel) {
  if (channel === 'landing_only') return 'Landing';
  if (channel === 'both') return 'CRM + Landing';
  return 'CRM';
}

function DemoLandingSettings({ demo, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [salespeople, setSalespeople] = useState([]);
  const [form, setForm] = useState({
    countries: demo.countries || [],
    channel: demo.channel || 'crm_only',
    capacity: demo.capacity || 12,
    assigned_sales_user_id: demo.assigned_sales_user_id || '',
    meeting_url: demo.meeting_url || '',
  });

  useEffect(() => {
    setForm({
      countries: demo.countries || [],
      channel: demo.channel || 'crm_only',
      capacity: demo.capacity || 12,
      assigned_sales_user_id: demo.assigned_sales_user_id || '',
      meeting_url: demo.meeting_url || '',
    });
  }, [demo.id, demo.countries, demo.channel, demo.capacity, demo.assigned_sales_user_id, demo.meeting_url]);

  useEffect(() => {
    if (!editing) return;
    demosService.getSalespeople()
      .then((res) => {
        const list = res?.data ?? res ?? [];
        setSalespeople(Array.isArray(list) ? list : []);
      })
      .catch(() => setSalespeople([]));
  }, [editing]);

  const handleSave = async () => {
    if (!form.countries?.length) {
      toast.error('Select at least one country');
      return;
    }
    const landing = form.channel === 'landing_only' || form.channel === 'both';
    if (landing && !form.assigned_sales_user_id) {
      toast.error('Salesperson is required for landing / both demos');
      return;
    }
    if (!hasMeetingUrl(form.meeting_url)) {
      toast.error('Demo join link is required');
      return;
    }
    try {
      setSaving(true);
      await demosService.updateDemo(demo.id, {
        countries: form.countries,
        channel: form.channel,
        capacity: Number(form.capacity) || 12,
        assigned_sales_user_id: form.assigned_sales_user_id || null,
        meeting_url: normalizeMeetingUrlInput(form.meeting_url),
      });
      toast.success('Demo landing settings saved');
      setEditing(false);
      if (onRefresh) await onRefresh(demo.id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save landing settings');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <Button size="small" startIcon={<EditIcon />} onClick={() => setEditing(true)} sx={{ mt: 1 }}>
        Edit channel, countries & join link
      </Button>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
        <InputLabel>Countries</InputLabel>
        <Select
          multiple
          value={form.countries}
          onChange={(e) => setForm((p) => ({ ...p, countries: e.target.value }))}
          input={<OutlinedInput label="Countries" />}
          renderValue={(selected) => (selected || []).join(', ')}
        >
          {getCountries().map((c) => (
            <MenuItem key={c} value={c}>{c}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <RadioGroup
        row
        value={form.channel}
        onChange={(e) => setForm((p) => ({ ...p, channel: e.target.value }))}
      >
        <FormControlLabel value="crm_only" control={<Radio size="small" />} label="CRM only" />
        <FormControlLabel value="landing_only" control={<Radio size="small" />} label="Landing only" />
        <FormControlLabel value="both" control={<Radio size="small" />} label="Both" />
      </RadioGroup>
      <TextField
        size="small"
        label="Capacity"
        type="number"
        inputProps={{ min: 1, max: 50 }}
        value={form.capacity}
        onChange={(e) => setForm((p) => ({ ...p, capacity: parseInt(e.target.value, 10) || 12 }))}
        sx={{ mr: 1, mt: 1, width: 120 }}
      />
      <FormControl size="small" sx={{ mt: 1, minWidth: 220 }} required={form.channel !== 'crm_only'}>
        <InputLabel>Salesperson</InputLabel>
        <Select
          value={form.assigned_sales_user_id}
          label="Salesperson"
          onChange={(e) => setForm((p) => ({ ...p, assigned_sales_user_id: e.target.value }))}
        >
          <MenuItem value=""><em>None</em></MenuItem>
          {salespeople.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.first_name} {s.last_name}
              {s.role && s.role !== 'sales' ? ` (${String(s.role).replace(/_/g, ' ')})` : ''}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>
          Changing salesperson does not reassign existing leads
        </FormHelperText>
      </FormControl>
      <TextField
        fullWidth
        size="small"
        label="Demo join link"
        placeholder="https://zoom.us/j/…"
        value={form.meeting_url}
        onChange={(e) => setForm((p) => ({ ...p, meeting_url: e.target.value }))}
        required
        error={!hasMeetingUrl(form.meeting_url)}
        sx={{ mt: 1.5 }}
        helperText="Required. Cannot be blank. Sent to the landing page with this demo"
      />
      <Box sx={{ mt: 1 }}>
        <Button size="small" variant="contained" onClick={handleSave} disabled={saving || !hasMeetingUrl(form.meeting_url)} sx={{ mr: 1 }}>
          Save
        </Button>
        <Button size="small" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
      </Box>
    </Box>
  );
}

function displayVal(v) {
  const s = v == null ? '' : String(v).trim();
  return s || '—';
}

function LeadField({ label, value, xs = 6, sm = 3 }) {
  return (
    <Grid item xs={xs} sm={sm}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
        {displayVal(value)}
      </Typography>
    </Grid>
  );
}

function LeadMetaStrip({ lead, dense = false }) {
  if (!lead) return null;
  const sm = dense ? 6 : 3;
  return (
    <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
      <LeadField label="Mother" value={lead.mother_name} sm={sm} />
      <LeadField label="Father" value={lead.father_name} sm={sm} />
      <LeadField label="Source" value={lead.source} sm={sm} />
      <LeadField label="Campaign" value={lead.campaign_name} sm={sm} />
    </Grid>
  );
}

const DemoDetails = ({ demo, onReschedule, onStatusUpdate, onRefresh }) => {
  const { user } = useAuth();
  const viewerTimezone = getViewerTimezone(user?.timezone);
  const [activeTab, setActiveTab] = useState(0);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [showStatusUpdateDialog, setShowStatusUpdateDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [unassigningLeadId, setUnassigningLeadId] = useState(null);
  const [unassigningStudentId, setUnassigningStudentId] = useState(null);

  const students = useMemo(() => {
    const raw = demo?.students;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch {
        return [];
      }
    }
    return [];
  }, [demo?.students]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleConvertLead = (lead) => {
    setSelectedLead(lead);
    setShowConvertDialog(true);
  };

  const handleUnassignLead = async (lead) => {
    if (!demo?.id || !lead?.id) return;
    try {
      setUnassigningLeadId(lead.id);
      await demosService.removeLeadFromDemo(demo.id, lead.id);
      toast.success(`${lead.first_name || 'Lead'} unassigned from demo.`);
      if (onRefresh && demo.id) await onRefresh(demo.id);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'Failed to unassign lead from demo';
      toast.error(msg);
    } finally {
      setUnassigningLeadId(null);
    }
  };

  const handleUnassignStudent = async (student) => {
    if (!demo?.id || !student?.id) return;
    try {
      setUnassigningStudentId(student.id);
      await demosService.removeStudentFromDemo(demo.id, student.id);
      toast.success(`${student.first_name || 'Student'} removed from demo.`);
      if (onRefresh && demo.id) await onRefresh(demo.id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove student from demo');
    } finally {
      setUnassigningStudentId(null);
    }
  };

  const handleCloseConvertDialog = () => {
    setShowConvertDialog(false);
    setSelectedLead(null);
  };

  const handleEnrollmentLinkSuccess = async () => {
    if (onRefresh && demo?.id) await onRefresh(demo.id);
  };

  const handleOpenStatusUpdate = async (lead) => {
    setSelectedLead(lead);
    setShowStatusUpdateDialog(true);
    if (!lead?.id) return;
    try {
      const res = await leadsAPI.getById(lead.id);
      const fullLead = res.data?.lead || res.data;
      if (fullLead?.id) {
        setSelectedLead((prev) => ({ ...(prev || {}), ...fullLead }));
      }
    } catch (error) {
      console.error('Error loading lead for status update:', error);
    }
  };

  const handleStatusUpdateComplete = () => {
    setShowStatusUpdateDialog(false);
    setSelectedLead(null);
    setActiveTab(1);
    if (onRefresh && demo?.id) onRefresh(demo.id);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      case 'rescheduled': return 'warning';
      default: return 'default';
    }
  };

  if (!demo) {
    return <Typography>No demo data available</Typography>;
  }

  return (
    <Box>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Overview" icon={<ScheduleIcon />} />
            <Tab label="Lead Details" icon={<PersonIcon />} />
            <Tab label="Teacher Details" icon={<SchoolIcon />} />
            <Tab label="History" icon={<HistoryIcon />} />
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Demo Information</Typography>
                    {demo.name && (
                      <Box mb={2}>
                        <Typography variant="body2" color="textSecondary">Demo Name</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{demo.name}</Typography>
                      </Box>
                    )}
                    <Box mb={2}>
                      <Typography variant="body2" color="textSecondary">Demo Type</Typography>
                      <Chip
                        label={demo.type === 'trial' ? 'Trial' : 'Demo'}
                        color={demo.type === 'trial' ? 'primary' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    <Box mb={2}>
                      <Typography variant="body2" color="textSecondary">Channel</Typography>
                      <Chip
                        size="small"
                        label={channelBadge(demo.channel)}
                        color={demo.channel === 'crm_only' || !demo.channel ? 'default' : 'primary'}
                        variant="outlined"
                      />
                      {(demo.countries || []).map((c) => (
                        <Chip key={c} size="small" label={c} sx={{ ml: 0.5 }} variant="outlined" />
                      ))}
                      <DemoLandingSettings demo={demo} onRefresh={onRefresh} />
                    </Box>
                    {(demo.capacity != null || demo.booked != null) && (
                      <Box mb={2}>
                        <Typography variant="body2" color="textSecondary">Capacity</Typography>
                        <Typography variant="body1">
                          {demo.booked != null ? demo.booked : (demo.lead_count || 0)} / {demo.capacity || '—'} unique leads
                        </Typography>
                      </Box>
                    )}
                    <Box mb={2}>
                      <Typography variant="body2" color="textSecondary">Salesperson</Typography>
                      <Typography variant="body1">
                        {demo.assigned_sales_first_name
                          ? `${demo.assigned_sales_first_name} ${demo.assigned_sales_last_name || ''}`.trim()
                          : (demo.assigned_sales_user_id ? 'Assigned' : 'None')}
                      </Typography>
                    </Box>
                    <Box mb={2}>
                      <Typography variant="body2" color="textSecondary">Status</Typography>
                      <Chip 
                        label={demo.status} 
                        color={getStatusColor(demo.status)}
                        size="small"
                      />
                    </Box>
                    <Box mb={2}>
                      <Typography variant="body2" color="textSecondary">Scheduled Date & Time</Typography>
                      <DemoCountryFullLines demo={demo} viewerTimezone={viewerTimezone} />
                    </Box>
                    <Box mb={2}>
                      <Typography variant="body2" color="textSecondary">Demo join link</Typography>
                      {demo.meeting_url ? (
                        <Typography
                          variant="body1"
                          component="a"
                          href={demo.meeting_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ wordBreak: 'break-all' }}
                        >
                          {demo.meeting_url}
                        </Typography>
                      ) : (
                        <Typography variant="body1" color="text.secondary">Not set</Typography>
                      )}
                    </Box>
                    <Box mb={2}>
                      <Typography variant="body2" color="textSecondary">Duration</Typography>
                      <Typography variant="body1">{demo.duration_minutes} minutes</Typography>
                    </Box>
                    <Box mb={2}>
                      <Typography variant="body2" color="textSecondary">Number of Leads</Typography>
                      <Typography variant="body1">{demo.lead_count || (demo.leads?.length || 0)}</Typography>
                    </Box>
                    <Box mb={2}>
                      <Typography variant="body2" color="textSecondary">Student performers</Typography>
                      <Typography variant="body1">
                        {demo.student_count ?? students.length ?? 0}
                      </Typography>
                    </Box>
                    {demo.notes && (
                      <Box mb={2}>
                        <Typography variant="body2" color="textSecondary">Notes</Typography>
                        <Typography variant="body1">{demo.notes}</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Student performers
                      {students.length > 0 && (
                        <Chip label={`${students.length}`} size="small" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    {students.length > 0 ? (
                      students.map((student, index) => (
                        <Box
                          key={student.id}
                          sx={{
                            mb: 2,
                            pb: 2,
                            borderBottom: index < students.length - 1 ? '1px solid #e0e0e0' : 'none',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              flexWrap: 'wrap',
                              gap: 1,
                            }}
                          >
                            <Box>
                              <Typography variant="body1">
                                <strong>Name:</strong> {student.first_name} {student.last_name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {student.display_id}
                                {student.batch_name ? ` · ${student.batch_name}` : ''}
                              </Typography>
                              {student.report_month && (
                                <Typography variant="body2" color="text.secondary">
                                  Demo-ready: {student.report_month}
                                </Typography>
                              )}
                            </Box>
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              startIcon={<PersonRemoveIcon />}
                              disabled={unassigningStudentId === student.id}
                              onClick={() => handleUnassignStudent(student)}
                            >
                              Remove
                            </Button>
                          </Box>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No student performers assigned. Add demo-ready students from the demos list.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Lead Information
                      {demo.leads && Array.isArray(demo.leads) && demo.leads.length > 1 && (
                        <Chip label={`${demo.leads.length} leads`} size="small" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    {demo.leads && Array.isArray(demo.leads) && demo.leads.length > 0 ? (
                      <Box>
                        {demo.leads.map((lead, index) => (
                          <Box key={lead.id} sx={{ mb: 2, pb: 2, borderBottom: index < demo.leads.length - 1 ? '1px solid #e0e0e0' : 'none' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                {lead.is_primary && (
                                  <Chip label="Primary" size="small" color="primary" />
                                )}
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                  {lead.first_name} {lead.last_name}
                                </Typography>
                              </Box>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={<PersonRemoveIcon />}
                                disabled={unassigningLeadId === lead.id}
                                onClick={() => handleUnassignLead(lead)}
                              >
                                Unassign
                              </Button>
                            </Box>
                            <Grid container spacing={1.5}>
                              <LeadField label="Age" value={formatChildAgeLabel(lead)} />
                              <LeadField label="Email" value={lead.email} />
                              <LeadField label="Phone" value={lead.phone} />
                              <LeadField label="Country" value={lead.country} />
                              <LeadField label="Timezone" value={lead.timezone} />
                            </Grid>
                            <LeadMetaStrip lead={lead} dense />
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Box>
                    <Typography variant="body1">
                      <strong>Name:</strong> {demo.lead_first_name} {demo.lead_last_name}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Email:</strong> {demo.lead_email}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Phone:</strong> {demo.lead_phone || 'N/A'}
                    </Typography>
                        {demo.age_group && (
                    <Typography variant="body1">
                      <strong>Age Group:</strong> {demo.age_group}
                    </Typography>
                        )}
                        {demo.lead_country && (
                    <Typography variant="body1">
                      <strong>Country:</strong> {demo.lead_country}
                    </Typography>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {activeTab === 1 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Lead Details
                {demo.leads && Array.isArray(demo.leads) && demo.leads.length > 1 && (
                  <Chip label={`${demo.leads.length} leads`} size="small" sx={{ ml: 1 }} />
                )}
              </Typography>
              {demo.leads && Array.isArray(demo.leads) && demo.leads.length > 0 ? (
                <Box>
                  {demo.leads.map((lead, index) => (
                    <Box key={lead.id} sx={{ mb: 3 }}>
                      {index > 0 && <Divider sx={{ my: 2 }} />}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                          {lead.is_primary && <Chip label="Primary" size="small" color="primary" />}
                          {lead.first_name} {lead.last_name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            startIcon={<PersonRemoveIcon />}
                            disabled={unassigningLeadId === lead.id}
                            onClick={() => handleUnassignLead(lead)}
                          >
                            Unassign from demo
                          </Button>
                          {lead.status !== 'won' && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => handleOpenStatusUpdate(lead)}
                            >
                              Update Status
                            </Button>
                          )}
                          {(lead.status === 'won') ? (
                            <Chip label="Enrolled" size="small" color="success" />
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<PersonAddIcon />}
                              onClick={() => handleConvertLead(lead)}
                            >
                              Generate enrollment link
                            </Button>
                          )}
                        </Box>
                      </Box>
                      {lead.status && (
                        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={lead.status}
                            size="small"
                            color={lead.status === 'won' ? 'success' : 'default'}
                            variant="outlined"
                          />
                          {lead.sub_status ? (
                            <Chip
                              label={lead.sub_status}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ) : null}
                        </Box>
                      )}
                      <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                        <LeadField label="Email" value={lead.email} />
                        <LeadField label="Phone" value={lead.phone} />
                        <LeadField label="Country" value={lead.country} />
                        <LeadField label="Timezone" value={lead.timezone} />
                      </Grid>
                      <LeadMetaStrip lead={lead} />
                      <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                        <LeadField label="Age" value={formatChildAgeLabel(lead)} />
                        <LeadField label="Age group" value={lead.age_group} />
                        <LeadField label="Student type" value={lead.student_type} />
                        <LeadField label="Class preference" value={lead.class_type_preference} />
                        <LeadField label="Learning level" value={lead.learning_level} />
                      </Grid>
                      {lead.emergency_contact_name && (
                        <Box sx={{ mt: 1.5 }}>
                          <Divider sx={{ mb: 1 }} />
                          <Typography variant="caption" color="text.secondary">Emergency contact</Typography>
                          <Typography variant="body2">
                            {lead.emergency_contact_name} · {lead.emergency_contact_phone || '—'}
                          </Typography>
                        </Box>
                      )}
                      {lead.support_requirements && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">Support requirements</Typography>
                          <Typography variant="body2">{lead.support_requirements}</Typography>
                        </Box>
                      )}
                      <Box sx={{ mt: 1.5 }}>
                        <Divider sx={{ mb: 1 }} />
                        {(lead.status === 'won') ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label="Enrolled" size="medium" color="success" />
                            <Typography variant="body2" color="text.secondary">
                              This lead has been converted to a student
                            </Typography>
                          </Box>
                        ) : (
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<PersonAddIcon />}
                            onClick={() => handleConvertLead(lead)}
                          >
                            Generate enrollment link
                          </Button>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">Personal Information</Typography>
                  <Typography variant="body1"><strong>Name:</strong> {demo.lead_first_name} {demo.lead_last_name}</Typography>
                  <Typography variant="body1"><strong>Email:</strong> {demo.lead_email}</Typography>
                  <Typography variant="body1"><strong>Phone:</strong> {demo.lead_phone || 'N/A'}</Typography>
                    {demo.lead_country && (
                  <Typography variant="body1"><strong>Country:</strong> {demo.lead_country}</Typography>
                    )}
                    {demo.lead_timezone && (
                  <Typography variant="body1"><strong>Timezone:</strong> {demo.lead_timezone}</Typography>
                    )}
                    {(demo.primary_lead || demo.leads?.[0]) && (
                  <Typography variant="body1"><strong>Age:</strong> {formatChildAgeLabel(demo.primary_lead || demo.leads[0])}</Typography>
                    )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">Academic Information</Typography>
                    {demo.age_group && (
                  <Typography variant="body1"><strong>Age Group:</strong> {demo.age_group}</Typography>
                    )}
                    {demo.student_type && (
                  <Typography variant="body1"><strong>Student Type:</strong> {demo.student_type}</Typography>
                    )}
                    {demo.class_type_preference && (
                  <Typography variant="body1"><strong>Class Preference:</strong> {demo.class_type_preference}</Typography>
                    )}
                    {demo.learning_level && (
                  <Typography variant="body1"><strong>Learning Level:</strong> {demo.learning_level}</Typography>
                    )}
                </Grid>
                {demo.emergency_contact_name && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" color="textSecondary">Emergency Contact</Typography>
                    <Typography variant="body1"><strong>Name:</strong> {demo.emergency_contact_name}</Typography>
                    <Typography variant="body1"><strong>Phone:</strong> {demo.emergency_contact_phone}</Typography>
                  </Grid>
                )}
                {demo.support_requirements && (
                  <Grid item xs={12}>
                    <Typography variant="body1"><strong>Support Requirements:</strong></Typography>
                    <Typography variant="body1">{demo.support_requirements}</Typography>
                  </Grid>
                )}
                {demo.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body1"><strong>Notes:</strong></Typography>
                    <Typography variant="body1">{demo.notes}</Typography>
                  </Grid>
                )}
                  {/* Send enrollment form Button or Enrolled status for single lead */}
                  {demo.lead_first_name && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      {((demo.lead_status || demo.status) === 'won') ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label="Enrolled" size="medium" color="success" />
                          <Typography variant="body2" color="text.secondary">
                            This lead has been converted to a student
                          </Typography>
                        </Box>
                      ) : (
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<PersonAddIcon />}
                          onClick={() => handleConvertLead({
                            id: demo.lead_id,
                            first_name: demo.lead_first_name,
                            last_name: demo.lead_last_name,
                            email: demo.lead_email,
                            phone: demo.lead_phone,
                            country: demo.lead_country,
                            timezone: demo.lead_timezone,
                            age_group: demo.age_group,
                            student_type: demo.student_type,
                            class_type_preference: demo.class_type_preference,
                            learning_level: demo.learning_level,
                            status: demo.lead_status || demo.status || 'new'
                          })}
                          sx={{ mt: 2 }}
                        >
                          Generate enrollment link
                        </Button>
                      )}
                    </Grid>
                  )}
              </Grid>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 2 && demo.teacher_first_name && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Teacher Details</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">Personal Information</Typography>
                  <Typography variant="body1"><strong>Name:</strong> {formatDemoInstructorLabel({
                    first_name: demo.teacher_first_name,
                    last_name: demo.teacher_last_name,
                    role: demo.teacher_role,
                  })}</Typography>
                  <Typography variant="body1"><strong>Email:</strong> {demo.teacher_email}</Typography>
                  <Typography variant="body1"><strong>Phone:</strong> {demo.teacher_phone || 'N/A'}</Typography>
                  <Typography variant="body1"><strong>Timezone:</strong> {demo.teacher_timezone}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">Professional Information</Typography>
                  <Typography variant="body1"><strong>Status:</strong> Assigned</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {activeTab === 3 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Demo History</Typography>
              <Typography variant="body2" color="textSecondary">
                Demo created on {formatDateTimeInViewerTZ(demo.created_at, viewerTimezone)}
              </Typography>
              {demo.updated_at && demo.updated_at !== demo.created_at && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Last updated on {formatDateTimeInViewerTZ(demo.updated_at, viewerTimezone)}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        <EnrollmentLinkDialog
          open={showConvertDialog}
          onClose={handleCloseConvertDialog}
          lead={selectedLead}
          onSuccess={handleEnrollmentLinkSuccess}
        />

        {/* Status Update Dialog */}
        <Dialog open={showStatusUpdateDialog} onClose={() => { setShowStatusUpdateDialog(false); setSelectedLead(null); }} maxWidth="sm" fullWidth>
          <DialogTitle>Update Lead Status</DialogTitle>
          <DialogContent>
            {selectedLead && (
              <StatusUpdateDialog
                lead={selectedLead}
                onUpdate={handleStatusUpdateComplete}
                onCancel={() => { setShowStatusUpdateDialog(false); setSelectedLead(null); }}
                isDialog={false}
              />
            )}
          </DialogContent>
        </Dialog>
    </Box>
  );
};

export default DemoDetails;
